<template>
  <div>
    <h2
      class="mt-0 tw-text-2xl tw-font-semibold tw-mb-2"
      style="color: var(--text-color)"
    >
      Ads.txt Dashboard
    </h2>

    <TabView v-model:activeIndex="activeTab">
      <!-- Tab 1: Overview -->
      <TabPanel header="Overview">
        <Card>
          <template #content>
            <DataTable
              :value="domains"
              class="p-datatable-sm"
              sortMode="multiple"
              :rowHover="true"
              @row-click="onOverviewRowClick"
              :rowClass="() => 'cursor-pointer'"
            >
              <Column field="domain" header="Domain" :sortable="true">
                <template #body="slotProps">
                  <div class="flex align-items-center gap-2">
                    <span class="font-semibold">{{
                      slotProps.data.domain
                    }}</span>
                    <Tag
                      v-if="slotProps.data.fetchFailed"
                      severity="danger"
                      value="FETCH FAILED"
                    />
                  </div>
                </template>
              </Column>
              <Column field="impressions" header="Loads (7d)" :sortable="true">
                <template #body="slotProps">
                  {{ formatNumber(slotProps.data.impressions) }}
                </template>
              </Column>
              <Column field="missingP1" header="P1" :sortable="true">
                <template #body="slotProps">
                  <span
                    :class="
                      slotProps.data.missingP1 > 0
                        ? 'text-red-500 font-bold'
                        : 'text-500'
                    "
                    >{{ slotProps.data.missingP1 }}</span
                  >
                </template>
              </Column>
              <Column field="missingP2" header="P2" :sortable="true">
                <template #body="slotProps">
                  <span
                    :class="
                      slotProps.data.missingP2 > 0
                        ? 'text-orange-400 font-bold'
                        : 'text-500'
                    "
                    >{{ slotProps.data.missingP2 }}</span
                  >
                </template>
              </Column>
              <Column field="missingP3" header="P3" :sortable="true">
                <template #body="slotProps">
                  <span
                    :class="slotProps.data.missingP3 > 0 ? '' : 'text-500'"
                    >{{ slotProps.data.missingP3 }}</span
                  >
                </template>
              </Column>
            </DataTable>
          </template>
        </Card>
      </TabPanel>

      <!-- Tab 2: Missing Lines -->
      <TabPanel header="Missing Lines">
        <!-- Summary cards -->
        <div class="grid mb-2">
          <div class="col-6 md:col-3">
            <Card class="summary-card">
              <template #content>
                <div class="text-center">
                  <div class="text-3xl font-bold">{{ domains.length }}</div>
                  <div class="text-500">Active Domains</div>
                </div>
              </template>
            </Card>
          </div>
          <div class="col-6 md:col-3">
            <Card class="summary-card">
              <template #content>
                <div class="text-center">
                  <div class="text-3xl font-bold text-red-500">
                    {{ p1DomainsCount }}
                  </div>
                  <div class="text-500">P1 Critical</div>
                </div>
              </template>
            </Card>
          </div>
          <div class="col-6 md:col-3">
            <Card class="summary-card">
              <template #content>
                <div class="text-center">
                  <div class="text-3xl font-bold text-orange-400">
                    {{ p2DomainsCount }}
                  </div>
                  <div class="text-500">P2 Warning</div>
                </div>
              </template>
            </Card>
          </div>
          <div class="col-6 md:col-3">
            <Card class="summary-card">
              <template #content>
                <div class="text-center">
                  <div class="text-3xl font-bold text-green-500">
                    {{ allClearDomains.length }}
                  </div>
                  <div class="text-500">All Clear</div>
                </div>
              </template>
            </Card>
          </div>
        </div>

        <!-- Filter bar -->
        <div class="flex flex-wrap align-items-center gap-2 mb-3">
          <SelectButton
            v-model="viewFilter"
            :options="viewOptions"
            optionLabel="label"
            optionValue="value"
            :allowEmpty="false"
          />
          <Dropdown
            v-model="domainFilter"
            :options="domainOptions"
            placeholder="All Domains"
            showClear
            filter
            class="w-full sm:w-16rem"
          />
          <Dropdown
            v-model="partnerFilter"
            :options="partnerOptions"
            placeholder="All Partners"
            showClear
            filter
            class="w-full sm:w-16rem"
          />
        </div>

        <!-- Copy bar -->
        <div
          class="flex flex-wrap align-items-center gap-2 mb-3 p-2 border-round surface-50 border-1 surface-border"
        >
          <SelectButton
            v-model="copyPriority"
            :options="copyPriorityOptions"
            optionLabel="label"
            optionValue="value"
            :allowEmpty="false"
          />
          <Button
            icon="pi pi-copy"
            :label="`Copy missing lines (${copyableLines.length})`"
            size="small"
            :disabled="!copyableLines.length"
            @click="copyLines(copyableLines, 'Missing lines')"
          />
        </div>

        <!-- Domain cards -->
        <div
          v-for="domain in issueDomains"
          :key="domain.domain"
          class="domain-card mb-3"
        >
          <div
            class="flex flex-wrap justify-content-between align-items-center mb-2 gap-2"
          >
            <div class="flex align-items-center gap-2 flex-wrap">
              <span class="text-xl font-bold">{{ domain.domain }}</span>
              <Tag
                :severity="domain.hasVideo ? 'info' : 'secondary'"
                :value="domain.hasVideo ? 'VIDEO + AUDIO' : 'AUDIO'"
              />
              <Tag
                v-if="domain.fetchFailed"
                severity="danger"
                value="ADS.TXT NOT FOUND"
              />
              <span class="text-500 text-sm">
                {{ formatNumber(domain.impressions) }} loads (7d)
              </span>
            </div>
            <div class="flex align-items-center gap-2">
              <span class="text-500 text-sm">
                {{ domain.sellers.map((s) => s.name).join(", ") }}
              </span>
              <Button
                icon="pi pi-copy"
                label="Copy full ads.txt"
                size="small"
                outlined
                @click="copyFullAdsTxt(domain)"
              />
            </div>
          </div>

          <!-- P1 section -->
          <div v-if="visibleLines(domain, 1).length" class="mb-2">
            <div class="flex justify-content-between align-items-center mb-1">
              <span class="text-red-500 font-semibold">
                <i class="pi pi-times-circle mr-1" />P1 Critical —
                {{ visibleLines(domain, 1).length }} missing
              </span>
              <Button
                icon="pi pi-copy"
                label="Copy P1"
                text
                size="small"
                @click="
                  copyLines(
                    visibleLines(domain, 1).map((l) => l.row),
                    `${domain.domain} P1 lines`
                  )
                "
              />
            </div>
            <div
              v-for="line in visibleLines(domain, 1)"
              :key="line.row"
              class="missing-line"
            >
              <Tag severity="danger" :value="line.partner" class="mr-2" />
              <Tag
                severity="secondary"
                :value="(line.type || 'audio').toUpperCase()"
                class="mr-2"
              />
              <code class="line-code">{{ line.row }}</code>
            </div>
          </div>

          <!-- P2 section -->
          <div v-if="visibleLines(domain, 2).length" class="mb-2">
            <div class="flex justify-content-between align-items-center mb-1">
              <span class="text-orange-400 font-semibold">
                <i class="pi pi-exclamation-triangle mr-1" />P2 High —
                {{ visibleLines(domain, 2).length }} missing
              </span>
              <Button
                icon="pi pi-copy"
                label="Copy P2"
                text
                size="small"
                @click="
                  copyLines(
                    visibleLines(domain, 2).map((l) => l.row),
                    `${domain.domain} P2 lines`
                  )
                "
              />
            </div>
            <div
              v-for="line in visibleLines(domain, 2)"
              :key="line.row"
              class="missing-line"
            >
              <Tag severity="warning" :value="line.partner" class="mr-2" />
              <Tag
                severity="secondary"
                :value="(line.type || 'audio').toUpperCase()"
                class="mr-2"
              />
              <code class="line-code">{{ line.row }}</code>
            </div>
          </div>

          <!-- Wrong relationship section -->
          <div v-if="visibleMismatches(domain).length" class="mb-2">
            <div class="flex justify-content-between align-items-center mb-1">
              <span class="text-purple-400 font-semibold">
                <i class="pi pi-sync mr-1" />Wrong relationship —
                {{ visibleMismatches(domain).length }} lines
              </span>
            </div>
            <div
              v-for="line in visibleMismatches(domain)"
              :key="line.row"
              class="missing-line"
            >
              <Tag severity="info" :value="line.partner" class="mr-2" />
              <Tag
                severity="secondary"
                :value="(line.type || 'audio').toUpperCase()"
                class="mr-2"
              />
              <code class="line-code">{{ line.row }}</code>
              <span class="text-purple-400 text-sm ml-2">
                listed as {{ line.found }}, should be {{ line.expected }}
              </span>
            </div>
          </div>

          <!-- P3 note -->
          <div v-if="visibleLines(domain, 3).length" class="text-500 text-sm">
            + {{ visibleLines(domain, 3).length }} P3 (low priority) lines
            missing
          </div>
        </div>

        <div
          v-if="!issueDomains.length && !loading"
          class="text-center text-500 p-4"
        >
          No domains with missing lines for the current filters.
        </div>

        <!-- All Clear section -->
        <div v-if="allClearDomains.length" class="mt-4">
          <h3 class="text-green-500">
            <i class="pi pi-check-circle mr-1" />All Clear ({{
              allClearDomains.length
            }})
          </h3>
          <div class="flex flex-wrap gap-2">
            <Chip
              v-for="domain in allClearDomains"
              :key="domain.domain"
              :label="`${domain.domain} (${formatNumber(domain.impressions)})`"
              icon="pi pi-copy"
              class="cursor-pointer"
              v-tooltip.bottom="'Copy full ads.txt'"
              @click="copyFullAdsTxt(domain)"
            />
          </div>
        </div>
      </TabPanel>

      <!-- Tab 3: All Lines Reference -->
      <TabPanel header="All Lines Reference">
        <Card>
          <template #content>
            <div class="flex flex-wrap gap-2 mb-3">
              <Dropdown
                v-model="refPriorityFilter"
                :options="priorityFilterOptions"
                optionLabel="label"
                optionValue="value"
                placeholder="All Priorities"
                showClear
                class="w-full sm:w-14rem"
              />
              <Dropdown
                v-model="refTypeFilter"
                :options="typeFilterOptions"
                optionLabel="label"
                optionValue="value"
                placeholder="All Types"
                showClear
                class="w-full sm:w-14rem"
              />
              <span class="p-input-icon-left flex-1">
                <i class="pi pi-search" />
                <InputText
                  v-model="refSearch"
                  placeholder="Search lines..."
                  class="w-full"
                />
              </span>
            </div>
            <DataTable
              :value="filteredRefRows"
              class="p-datatable-sm"
              sortMode="multiple"
              paginator
              :rows="50"
            >
              <Column field="priority" header="Priority" :sortable="true">
                <template #body="slotProps">
                  <Tag
                    :severity="
                      priorityList[slotProps.data.priority - 1]?.severity
                    "
                    :value="priorityList[slotProps.data.priority - 1]?.label"
                  />
                </template>
              </Column>
              <Column
                field="partner_name"
                header="Partner"
                :sortable="true"
              ></Column>
              <Column field="type" header="Type" :sortable="true"></Column>
              <Column field="row_string" header="Row String" :sortable="true">
                <template #body="slotProps">
                  <code class="line-code">{{ slotProps.data.row_string }}</code>
                </template>
              </Column>
            </DataTable>
          </template>
        </Card>
      </TabPanel>
    </TabView>
  </div>
</template>

<script setup>
import { ref, computed, onBeforeMount } from "vue";
import { useStore } from "vuex";
import { useToast } from "primevue/usetoast";
import axios from "axios";
import TabView from "primevue/tabview";
import TabPanel from "primevue/tabpanel";
import Card from "primevue/card";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import Dropdown from "primevue/dropdown";
import SelectButton from "primevue/selectbutton";
import InputText from "primevue/inputtext";
import Button from "primevue/button";
import Tag from "primevue/tag";
import Chip from "primevue/chip";
import { API_BASE_URL, adsTxtFixed } from "@/consts";

const store = useStore();
const toast = useToast();
const token = localStorage.getItem("adminToken");

const activeTab = ref(0);
const loading = ref(false);
const domains = ref([]);
const adsTxtRows = ref([]);

// Tab 2 filters
const viewFilter = ref("all");
const domainFilter = ref(null);
const partnerFilter = ref(null);
const copyPriority = ref("p1p2");

const viewOptions = [
  { label: "All", value: "all" },
  { label: "P1 Only", value: "p1" },
  { label: "P2 Only", value: "p2" },
];

const copyPriorityOptions = [
  { label: "P1 + P2", value: "p1p2" },
  { label: "P1 only", value: "p1" },
  { label: "P2 only", value: "p2" },
];

// Tab 3 filters
const refPriorityFilter = ref(null);
const refTypeFilter = ref(null);
const refSearch = ref("");

const priorityList = [
  { label: "Critical", severity: "danger" },
  { label: "High", severity: "warning" },
  { label: "Low", severity: "secondary" },
];

const priorityFilterOptions = [
  { label: "P1 — Critical", value: 1 },
  { label: "P2 — High", value: 2 },
  { label: "P3 — Low", value: 3 },
];

const typeFilterOptions = [
  { label: "Audio", value: "audio" },
  { label: "Display", value: "display" },
  { label: "Video", value: "video" },
  { label: "App Audio", value: "app_audio" },
  { label: "App Display", value: "app_display" },
  { label: "App Video", value: "app_video" },
];

const formatNumber = (value) => Number(value || 0).toLocaleString();

// ---- Tab 1 & 2 data ----

const p1DomainsCount = computed(
  () => domains.value.filter((d) => d.missingP1 > 0).length
);
const p2DomainsCount = computed(
  () => domains.value.filter((d) => d.missingP1 === 0 && d.missingP2 > 0).length
);

const domainOptions = computed(() => domains.value.map((d) => d.domain));

const partnerOptions = computed(() => {
  const partners = new Set();
  domains.value.forEach((d) =>
    d.missingLines.forEach((line) => partners.add(line.partner))
  );
  return [...partners].sort();
});

// Lines of one priority for a domain card, respecting partner + view filters
const visibleLines = (domain, priority) => {
  if (viewFilter.value === "p1" && priority !== 1) return [];
  if (viewFilter.value === "p2" && priority !== 2) return [];
  return domain.missingLines.filter((line) => {
    const linePriority = line.priority >= 3 ? 3 : line.priority;
    if (linePriority !== priority) return false;
    if (partnerFilter.value && line.partner !== partnerFilter.value)
      return false;
    return true;
  });
};

// Wrong-relationship lines for a domain card, respecting partner + view filters
const visibleMismatches = (domain) => {
  return (domain.mismatches || []).filter((line) => {
    const linePriority = line.priority >= 3 ? 3 : line.priority;
    if (viewFilter.value === "p1" && linePriority !== 1) return false;
    if (viewFilter.value === "p2" && linePriority !== 2) return false;
    if (partnerFilter.value && line.partner !== partnerFilter.value)
      return false;
    return true;
  });
};

// Domains shown as issue cards in Tab 2
const issueDomains = computed(() => {
  return domains.value.filter((domain) => {
    if (domainFilter.value && domain.domain !== domainFilter.value)
      return false;
    return (
      visibleLines(domain, 1).length > 0 ||
      visibleLines(domain, 2).length > 0 ||
      (viewFilter.value === "all" && visibleLines(domain, 3).length > 0) ||
      visibleMismatches(domain).length > 0 ||
      domain.fetchFailed
    );
  });
});

const allClearDomains = computed(() =>
  domains.value.filter(
    (d) =>
      d.missingP1 === 0 &&
      d.missingP2 === 0 &&
      !(d.mismatches || []).length &&
      !d.fetchFailed &&
      (!domainFilter.value || d.domain === domainFilter.value)
  )
);

// Lines matching the copy-bar priority toggle across visible domain cards
const copyableLines = computed(() => {
  const priorities =
    copyPriority.value === "p1"
      ? [1]
      : copyPriority.value === "p2"
      ? [2]
      : [1, 2];
  const lines = [];
  const seen = new Set();
  issueDomains.value.forEach((domain) => {
    priorities.forEach((priority) => {
      visibleLines(domain, priority).forEach((line) => {
        if (!seen.has(line.row)) {
          seen.add(line.row);
          lines.push(line.row);
        }
      });
    });
  });
  return lines;
});

// Full expected ads.txt for a domain: group direct lines + demand lines
// of the relevant types, scoped to the copy-bar priority toggle
const copyFullAdsTxt = (domain) => {
  const priorities =
    copyPriority.value === "p1"
      ? [1]
      : copyPriority.value === "p2"
      ? [2]
      : [1, 2];
  const types = domain.hasVideo ? ["audio", "video"] : ["audio"];

  const directLines = adsTxtFixed(domain.sellerGroupId, domain.displayId)
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);

  const demandLines = adsTxtRows.value
    .filter(
      (row) =>
        types.includes(row.type) &&
        priorities.includes(Number(row.priority) || 3)
    )
    .map((row) => row.row_string.trim());

  copyLines([...directLines, ...demandLines], `${domain.domain} full ads.txt`);
};

const copyLines = (lines, label) => {
  navigator.clipboard
    .writeText(lines.join("\n"))
    .then(() => {
      toast.add({
        severity: "success",
        summary: "Copied!",
        detail: `${label} copied to clipboard (${lines.length} lines)`,
        life: 2000,
      });
    })
    .catch(() => {
      toast.add({
        severity: "error",
        summary: "Error",
        detail: "Failed to copy to clipboard",
        life: 3000,
      });
    });
};

const onOverviewRowClick = (event) => {
  domainFilter.value = event.data.domain;
  activeTab.value = 1;
};

// ---- Tab 3 data ----

const filteredRefRows = computed(() => {
  return adsTxtRows.value.filter((row) => {
    if (refPriorityFilter.value && row.priority !== refPriorityFilter.value)
      return false;
    if (refTypeFilter.value && row.type !== refTypeFilter.value) return false;
    if (refSearch.value) {
      const query = refSearch.value.toLowerCase();
      if (
        !row.row_string?.toLowerCase().includes(query) &&
        !row.partner_name?.toLowerCase().includes(query)
      )
        return false;
    }
    return true;
  });
});

// ---- Data fetching ----

const fetchDashboard = async () => {
  try {
    loading.value = true;
    store.commit("setLoading", true);
    const response = await axios.get(
      `${API_BASE_URL}/adstxt-dashboard/overview`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    domains.value = response.data?.data?.domains || [];
  } catch (error) {
    console.error("Error fetching ads.txt dashboard:", error);
    toast.add({
      severity: "error",
      summary: "Error",
      detail: "Failed to fetch ads.txt dashboard data.",
      life: 4000,
    });
  } finally {
    loading.value = false;
    store.commit("setLoading", false);
  }
};

const fetchAdsTxtRows = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}/adstxt-rows`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    adsTxtRows.value = response.data?.result || [];
  } catch (error) {
    console.error("Error fetching ads.txt rows:", error);
    toast.add({
      severity: "error",
      summary: "Error",
      detail: "Failed to fetch ads.txt rows.",
      life: 4000,
    });
  }
};

onBeforeMount(() => {
  fetchDashboard();
  fetchAdsTxtRows();
});
</script>

<style scoped>
.summary-card :deep(.p-card-content) {
  padding: 0;
}

.domain-card {
  background: var(--surface-card);
  border: 1px solid var(--surface-border);
  border-radius: 8px;
  padding: 1rem;
}

.missing-line {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.25rem;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  margin-bottom: 0.25rem;
  background: var(--surface-50);
}

.line-code {
  font-family: "Courier New", monospace;
  font-size: 0.85rem;
  word-break: break-all;
}

:deep(.cursor-pointer) {
  cursor: pointer;
}
</style>
